import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Users,
  UserPlus,
  CreditCard,
  ShieldCheck,
  ShieldAlert,
  Search,
  RefreshCw,
  Loader2,
  Beaker,
  Ban,
  CheckCircle2,
  BarChart3,
} from "lucide-react";

const PAGE_SIZE = 50;

/**
 * /admin/users — Phase 2.1K + 2.1K.1 admin users dashboard.
 *
 * Access:
 * - Backend enforces admin via `require_admin` (ADMIN_EMAILS env or
 *   fallback owner identity). The frontend only *hides* the nav link
 *   and redirects non-admins away from this route — it does NOT rely
 *   on frontend-only checks for data access.
 * - Summary cards are clickable filters. Clicking a card fetches the
 *   filtered page from the backend (never client-side over the current
 *   page).
 * - Block/Unblock actions call admin endpoints; blocked users are
 *   rejected by `get_current_user` on their next protected request.
 */
const FILTER_LABELS = {
  all: "All users",
  registered_today: "Heute registriert",
  paying: "Paying User",
  free: "Free User",
  test: "Test User",
  blocked: "Blocked",
};

export default function AdminUsers() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pendingUserId, setPendingUserId] = useState("");
  const [blockDialog, setBlockDialog] = useState({ open: false, user: null });
  const [blockReason, setBlockReason] = useState("");
  const [blockError, setBlockError] = useState("");

  const fetchSummary = useCallback(async () => {
    try {
      const r = await api.get(`/admin/users/summary`);
      setSummary(r.data);
    } catch (e) {
      setSummary(null);
    }
  }, []);

  const fetchUsers = useCallback(async (opts = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: opts.offset ?? 0,
      };
      // Callers MUST pass ``opts.filter`` and ``opts.search`` explicitly
      // (or ``null``/``""`` to clear). ``fetchUsers`` is intentionally
      // stable — it does NOT close over ``activeFilter`` or ``search``
      // state, so the mount-effect below cannot re-fire and clobber a
      // filter click. See Phase 2.1K.2 regression fix.
      const filterArg = opts.filter;
      if (filterArg && filterArg !== "all") {
        params.filter = filterArg;
      }
      const searchArg = opts.search;
      if (searchArg) params.search = searchArg;
      const r = await api.get(`/admin/users`, { params });
      setUsers(r.data.users || []);
      setTotal(r.data.total || 0);
      setOffset(r.data.offset || 0);
    } catch (e) {
      setError(
        e?.response?.status === 403
          ? "Admin access required."
          : "Failed to load users."
      );
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchUsers({ offset: 0, filter: "all", search: "" });
  }, [fetchSummary, fetchUsers]);

  if (user && user.is_admin !== true) {
    return <Navigate to="/dashboard" replace />;
  }

  const applyFilter = (nextFilter) => {
    setActiveFilter(nextFilter);
    fetchUsers({ offset: 0, filter: nextFilter, search });
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const s = searchInput.trim();
    setSearch(s);
    fetchUsers({ offset: 0, filter: activeFilter, search: s });
  };

  const onClearSearch = () => {
    setSearch("");
    setSearchInput("");
    fetchUsers({ offset: 0, filter: activeFilter, search: "" });
  };

  const onLoadMore = () => {
    const next = offset + PAGE_SIZE;
    if (next >= total) return;
    const params = { limit: PAGE_SIZE, offset: next };
    if (activeFilter !== "all") params.filter = activeFilter;
    if (search) params.search = search;
    api
      .get(`/admin/users`, { params })
      .then((r) => {
        setUsers((prev) => [...prev, ...(r.data.users || [])]);
        setOffset(r.data.offset || next);
      })
      .catch(() => {});
  };

  const openBlockDialog = (u) => {
    setBlockReason("");
    setBlockError("");
    setBlockDialog({ open: true, user: u });
  };

  const confirmBlock = async () => {
    if (!blockDialog.user) return;
    setBlockError("");
    setPendingUserId(blockDialog.user.id);
    try {
      const r = await api.post(
        `/admin/users/${blockDialog.user.id}/block`,
        { reason: blockReason || undefined },
      );
      setUsers((prev) =>
        prev.map((x) =>
          x.id === blockDialog.user.id
            ? {
                ...x,
                is_blocked: true,
                blocked_at: r.data.blocked_at,
                blocked_reason: r.data.blocked_reason,
              }
            : x,
        ),
      );
      setBlockDialog({ open: false, user: null });
      setBlockReason("");
    } catch (e) {
      setBlockError(
        e?.response?.data?.detail ||
          e?.response?.data?.message ||
          "Block failed. Please try again.",
      );
    } finally {
      setPendingUserId("");
    }
  };

  const unblock = async (u) => {
    if (!u.id) return;
    setPendingUserId(u.id);
    try {
      const r = await api.post(`/admin/users/${u.id}/unblock`);
      setUsers((prev) =>
        prev.map((x) =>
          x.id === u.id
            ? { ...x, is_blocked: false, blocked_reason: null, blocked_at: null }
            : x,
        ),
      );
      void r;
    } catch (e) {
      // Non-fatal — reload the table so state matches server.
      fetchUsers({ offset: 0, filter: activeFilter, search });
    } finally {
      setPendingUserId("");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-400" />
            Admin — Users
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Registered users, credit balances and subscription status.
            Times are UTC. Click a card to filter the table.
          </p>
          <nav className="flex items-center gap-1 mt-3" data-testid="admin-tab-nav">
            <span
              className="text-[11px] font-mono uppercase tracking-wider px-3 py-1 rounded border border-blue-400/40 bg-blue-400/10 text-blue-300"
              data-testid="admin-tab-users-active"
            >
              Users
            </span>
            <Link
              to="/admin/profitability"
              className="text-[11px] font-mono uppercase tracking-wider px-3 py-1 rounded border border-zinc-800 text-zinc-400 hover:border-blue-400/40 hover:text-blue-300 transition-colors flex items-center gap-1"
              data-testid="admin-tab-profitability-link"
            >
              <BarChart3 className="w-3 h-3" /> Profitability
            </Link>
          </nav>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            fetchSummary();
            fetchUsers({ offset: 0, filter: activeFilter, search });
          }}
          data-testid="admin-users-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Summary cards (clickable filters). */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="admin-users-summary">
        <SummaryCard
          testId="summary-registered-today"
          icon={<UserPlus className="w-4 h-4 text-emerald-400" />}
          label="Heute registriert"
          value={summary?.registered_today}
          active={activeFilter === "registered_today"}
          onClick={() => applyFilter("registered_today")}
        />
        <SummaryCard
          testId="summary-total-users"
          icon={<Users className="w-4 h-4 text-blue-400" />}
          label="Gesamtuser"
          value={summary?.total_users}
          active={activeFilter === "all"}
          onClick={() => applyFilter("all")}
        />
        <SummaryCard
          testId="summary-paying-users"
          icon={<CreditCard className="w-4 h-4 text-violet-400" />}
          label="Paying User"
          value={summary?.paying_users}
          active={activeFilter === "paying"}
          onClick={() => applyFilter("paying")}
        />
        <SummaryCard
          testId="summary-free-users"
          icon={<Users className="w-4 h-4 text-zinc-400" />}
          label="Free User"
          value={summary?.free_users}
          active={activeFilter === "free"}
          onClick={() => applyFilter("free")}
        />
        <SummaryCard
          testId="summary-test-users"
          icon={<Beaker className="w-4 h-4 text-amber-400" />}
          label="Test User"
          value={summary?.test_users}
          active={activeFilter === "test"}
          onClick={() => applyFilter("test")}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="text-xs text-zinc-400" data-testid="admin-users-active-filter">
          Active filter:{" "}
          <span className="text-zinc-100 font-mono">
            {FILTER_LABELS[activeFilter] || activeFilter}
          </span>
          {activeFilter !== "all" && (
            <button
              type="button"
              className="ml-2 text-blue-400 hover:text-blue-300 underline"
              onClick={() => applyFilter("all")}
              data-testid="admin-users-clear-filter-btn"
            >
              clear filter
            </button>
          )}
        </div>
        <form
          onSubmit={onSearchSubmit}
          className="flex gap-2 min-w-[280px]"
          data-testid="admin-users-search-form"
        >
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by email…"
              className="pl-8 bg-[#0F1117] border-[#1E212B] text-sm"
              data-testid="admin-users-search-input"
            />
          </div>
          <Button type="submit" size="sm" data-testid="admin-users-search-btn">
            Search
          </Button>
          {search && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClearSearch}
              data-testid="admin-users-clear-search-btn"
            >
              Clear
            </Button>
          )}
        </form>
      </div>

      {/* Users table */}
      <div
        className="overflow-x-auto border border-[#1E212B] rounded-lg bg-[#0F1117]"
        data-testid="admin-users-table-wrap"
      >
        <table className="min-w-full text-sm" data-testid="admin-users-table">
          <thead className="bg-[#12141C] text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Email</th>
              <th className="text-left px-3 py-2 font-medium">Registered</th>
              <th className="text-right px-3 py-2 font-medium">Credits</th>
              <th className="text-left px-3 py-2 font-medium">Plan</th>
              <th className="text-left px-3 py-2 font-medium">Subscription</th>
              <th className="text-left px-3 py-2 font-medium">Last login</th>
              <th className="text-right px-3 py-2 font-medium">Scripts</th>
              <th className="text-left px-3 py-2 font-medium">Role</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-right px-3 py-2 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 && (
              <tr data-testid="admin-users-loading">
                <td colSpan={10} className="px-3 py-6 text-center text-zinc-500">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Loading…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr data-testid="admin-users-error">
                <td colSpan={10} className="px-3 py-6 text-center text-red-400">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && users.length === 0 && (
              <tr data-testid="admin-users-empty">
                <td colSpan={10} className="px-3 py-6 text-center text-zinc-500">
                  No users found.
                </td>
              </tr>
            )}
            {users.map((u) => (
              <UserRow
                key={u.id || u.email}
                u={u}
                pending={pendingUserId === u.id}
                onBlock={() => openBlockDialog(u)}
                onUnblock={() => unblock(u)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 text-xs text-zinc-400">
        <span data-testid="admin-users-pagination-info">
          Showing {users.length} of {total}
        </span>
        {offset + PAGE_SIZE < total && (
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            data-testid="admin-users-load-more-btn"
          >
            Load more
          </Button>
        )}
      </div>

      {/* Block confirmation dialog */}
      <Dialog
        open={blockDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setBlockDialog({ open: false, user: null });
            setBlockReason("");
            setBlockError("");
          }
        }}
      >
        <DialogContent data-testid="admin-users-block-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300">
              <Ban className="w-4 h-4" />
              Block user
            </DialogTitle>
            <DialogDescription>
              This will immediately prevent{" "}
              <span className="font-mono text-zinc-100">
                {blockDialog.user?.email}
              </span>{" "}
              from generating, updating, repairing scripts and from
              accessing their dashboard. Existing sessions cannot bypass this.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-zinc-400">
              Reason (optional, max 240 chars)
            </label>
            <Textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="chargeback / abuse / manual …"
              maxLength={240}
              rows={3}
              data-testid="admin-users-block-reason-input"
            />
            {blockError && (
              <p className="text-xs text-red-400" data-testid="admin-users-block-error">
                {typeof blockError === "string"
                  ? blockError
                  : JSON.stringify(blockError)}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setBlockDialog({ open: false, user: null });
                setBlockReason("");
                setBlockError("");
              }}
              data-testid="admin-users-block-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmBlock}
              disabled={pendingUserId === blockDialog.user?.id}
              data-testid="admin-users-block-confirm-btn"
            >
              {pendingUserId === blockDialog.user?.id ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Blocking…
                </>
              ) : (
                <>Block user</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ testId, icon, label, value, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border p-3 text-left transition-colors ${
        active
          ? "border-blue-500/60 bg-blue-500/5"
          : "border-[#1E212B] bg-[#0F1117] hover:border-blue-500/40 hover:bg-[#12141C]"
      }`}
      data-testid={testId}
      data-active={active ? "true" : "false"}
    >
      <div className="flex items-center gap-2 text-xs text-zinc-400 mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-2xl font-mono text-zinc-100">
        {value === null || value === undefined ? "—" : value}
      </div>
    </button>
  );
}

function UserRow({ u, pending, onBlock, onUnblock }) {
  const blocked = u.is_blocked === true;
  return (
    <tr
      className={`border-t border-[#1E212B] hover:bg-[#12141C] ${
        blocked ? "opacity-70" : ""
      }`}
      data-testid={`admin-user-row-${u.email || u.id}`}
    >
      <td className="px-3 py-2 text-zinc-100">
        {u.email || "—"}
        {blocked && (
          <ShieldAlert
            className="inline w-3.5 h-3.5 ml-1 text-red-400"
            aria-label="blocked"
          />
        )}
      </td>
      <td className="px-3 py-2 text-zinc-400 font-mono text-xs">
        {formatDate(u.created_at)}
      </td>
      <td className="px-3 py-2 text-right font-mono">{u.credits ?? 0}</td>
      <td className="px-3 py-2">
        <PlanBadge value={u.plan} />
      </td>
      <td className="px-3 py-2">
        <SubStatusBadge value={u.subscription_status} />
      </td>
      <td className="px-3 py-2 text-zinc-400 font-mono text-xs">
        {formatDate(u.last_login_at)}
      </td>
      <td className="px-3 py-2 text-right font-mono">
        {u.generated_scripts_count ?? 0}
      </td>
      <td className="px-3 py-2">
        <RoleBadge value={u.role} />
      </td>
      <td className="px-3 py-2">
        <StatusBadge blocked={blocked} />
      </td>
      <td className="px-3 py-2 text-right">
        {u.role === "admin" ? (
          <span
            className="text-[10px] text-zinc-500 font-mono uppercase"
            data-testid={`admin-user-row-${u.email}-protected`}
          >
            Protected
          </span>
        ) : blocked ? (
          <Button
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={onUnblock}
            data-testid={`admin-user-row-${u.email}-unblock-btn`}
            className="h-7 text-[11px]"
          >
            {pending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                <CheckCircle2 className="w-3 h-3 mr-1" /> Unblock
              </>
            )}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={onBlock}
            data-testid={`admin-user-row-${u.email}-block-btn`}
            className="h-7 text-[11px] border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200"
          >
            {pending ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                <Ban className="w-3 h-3 mr-1" /> Block
              </>
            )}
          </Button>
        )}
      </td>
    </tr>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toISOString().replace("T", " ").slice(0, 16) + " UTC";
  } catch {
    return "—";
  }
}

function PlanBadge({ value }) {
  const v = (value || "free").toLowerCase();
  const cls =
    v === "free"
      ? "bg-zinc-500/10 text-zinc-300 border-zinc-500/30"
      : "bg-blue-500/10 text-blue-300 border-blue-500/30";
  return (
    <span
      className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-mono uppercase ${cls}`}
    >
      {v}
    </span>
  );
}

function SubStatusBadge({ value }) {
  const v = (value || "unknown").toLowerCase();
  const palette = {
    active: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    trialing: "bg-blue-500/10 text-blue-300 border-blue-500/30",
    past_due: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    canceled: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
    unpaid: "bg-red-500/10 text-red-300 border-red-500/30",
    free: "bg-zinc-500/10 text-zinc-300 border-zinc-500/30",
  };
  const cls = palette[v] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/30";
  return (
    <span
      className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-mono uppercase ${cls}`}
    >
      {v}
    </span>
  );
}

function RoleBadge({ value }) {
  const v = (value || "user").toLowerCase();
  const cls =
    v === "admin"
      ? "bg-violet-500/10 text-violet-300 border-violet-500/30"
      : "bg-zinc-500/10 text-zinc-300 border-zinc-500/30";
  return (
    <span
      className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-mono uppercase ${cls}`}
    >
      {v}
    </span>
  );
}

function StatusBadge({ blocked }) {
  return blocked ? (
    <span className="inline-block text-[10px] px-1.5 py-0.5 rounded border font-mono uppercase bg-red-500/10 text-red-300 border-red-500/30">
      Blocked
    </span>
  ) : (
    <span className="inline-block text-[10px] px-1.5 py-0.5 rounded border font-mono uppercase bg-emerald-500/10 text-emerald-300 border-emerald-500/30">
      Active
    </span>
  );
}
